import { createApi } from '@reduxjs/toolkit/query/react';
import { createDownloadFileHook } from 'hooks/useDownloadFile';
import { formatError } from '_helpers/apiError';
import { crBaseQuery, crV2Fetch } from '_helpers/baseQuery';
import { downloadFileWithLink } from '_helpers/common';
import { alertActions } from '_reducers/alerts.reducer';
import {
  efeActions,
  efePagingActions,
  efeUiActions,
} from '_reducers/edgeFileExportReducer';

/**
 * @public
 * @name edgeFileExportApi
 * @description Creates edge file export api defination.
 */
export const efeApi = createApi({
  baseQuery: crBaseQuery(),
  reducerPath: 'edgeFileExportApi',
  endpoints: (builder) => ({
    /** @name getEdgeFileExportRequests */
    getEfeRequests: builder.query({
      query: (request) => {
        const query = {
          page: request?.page ?? 1,
          machineID: request?.machineID ?? 0,
        };
        return `/v1/edgefileexports?${new URLSearchParams(query).toString()}`;
      },
      onQueryStarted: async ({ machineID }, { dispatch, queryFulfilled }) => {
        /**
         * `onStart` side-effect
         * In case of edge file exports search term is machine id.
         * Because we want to fetch records by machine. With each fetch request only the page number will increase.
         */
        dispatch(efePagingActions.requestPage(machineID));

        try {
          const result = await queryFulfilled;

          /**
           * `onSuccess` side-effect
           *    - Dispatch items payload to store in entities state.
           *    - Prepare a payload to cache the paged data and dispatch.
           */
          await dispatch(efeActions.addEdgeFileExports(result.data.items));
          const params = new URL(result.meta.request.url).searchParams;

          const payload = {
            page: params.get('page'),

            /**
             * In case of edge file exports search term is machine id.
             */
            searchTerm: params.get('machineID'),

            /**
             * For more reference to why only entities id's are being dispatched.
             * @see createPaginationSlice function documentation to understand the store state structure.
             */
            data: result.data.items.map((m) => m.edgeFileExportID),
          };

          dispatch(efePagingActions.addPage(payload));
        } catch (e) {
          /**
           * `onError` side-effect
           */
          dispatch(
            alertActions.addFailure('Error fetching edge file export requests.')
          );
          dispatch(alertActions.addFailure(`${formatError(e)}`));
        }
      },
    }),
    /** @name createEdgeFileExportRequest */
    createEfeRequest: builder.mutation({
      query: (body) => ({
        url: '/v1/edgefileexports',
        method: 'POST',
        body,
      }),
      // trigger side effects or optimistic updates
      onQueryStarted: async (body, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;

          /**
           * `onSuccess` side-effect
           * - Invalidate the current paged data.
           * - Invalidate cached api state.
           * - Notify user of call success.
           */
          dispatch(efePagingActions.clearPagedData());
          dispatch(efeApi.util.resetApiState()); // There should be a better alternative. May be look into invalidateTags
          dispatch(
            alertActions.addSuccess('New edge file export request created.')
          );
        } catch (e) {
          /**
           * `onError` side-effect
           */
          dispatch(
            alertActions.addFailure(
              'Failed to create edge file export request.'
            )
          );
        }
      },
    }),
  }),
});

/**
 * @private
 * @description used to download the edge files from cloud.
 * @returns function that accepts edgeFileExportID to download the file.
 */
const downloadEdgeFile = (edgeFileExportID) => async (dispatch) => {
  const request = {
    method: 'GET',
    url: `/v1/edgefileexports/${edgeFileExportID}/downloadLink`,
  };

  try {
    /**
     * `beforeQuery` dispatch status to notify component that download has started.
     */
    dispatch(efeUiActions.downloadingFile(true));
    const resp = await dispatch(crV2Fetch(request));
    const data = await resp.json();
    /**
     * `onSuccess`
     * - download the file.
     * - dispatch status to notify component that dowload is finished.
     */
    if (data?.fileLink) {
      downloadFileWithLink(data.fileLink, `${edgeFileExportID}`);
      dispatch(alertActions.addSuccess('File download started.'));
    } else {
      dispatch(alertActions.addFailure('Failed to download file.'));
    }
  } catch (error) {
    /**
     * `onError`
     * - dispatch failure error.
     * - dispatch status to notify component that dowload is finished.
     */
    const message = formatError(error);
    dispatch(alertActions.addFailure('Failed to download file.'));
    dispatch(alertActions.addFailure(`${message}`));
  }
  dispatch(efeUiActions.downloadingFile(false));
};

/**
 * Hook to download edge file export request file.
 */
export const useDownloadEdgeFile = createDownloadFileHook(downloadEdgeFile);
export const { useGetEfeRequestsQuery, useCreateEfeRequestMutation } = efeApi;
